import React from 'react';
import { ACCENT_TOKENS } from '@shared/accents.ts';
import { prefersDarkText } from '@shared/ink.ts';
import { accentName, colourHex } from '../lib/format.ts';
import { cssVars } from '../lib/css.ts';

export { ACCENT_TOKENS };

/** Offered in the pickers but NOT dropped from `ACCENT_TOKENS` itself: SCARLET
 *  stays a legal accent everywhere else (existing deck source, the AI schemas,
 *  the library colour cycle whose tests assert token positions), it just isn't
 *  something we hand people a one-click button for. */
const HIDDEN_ACCENT_TOKENS = ['SCARLET'];

/** The dots to draw. A slide or section already ON a hidden token still shows
 *  its swatch — otherwise the picker would ring nothing and read as "no colour
 *  set" on a deck that plainly has one. */
function offeredTokens(value: string | null | undefined) {
  return ACCENT_TOKENS.filter((token) => !HIDDEN_ACCENT_TOKENS.includes(token) || token === value);
}

const HEX_RE = /^#[0-9a-f]{6}$/i;
const SHORT_HEX_RE = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;

/** Accept what people actually type — `faf553`, `#FAF553`, `#fa5` — and return
 *  the one canonical form the codecs store, or null when it isn't a colour. */
function parseHex(input: string | null | undefined): string | null {
  const raw = String(input || '').trim();
  if (!raw) return null;
  const short = raw.match(SHORT_HEX_RE);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  return HEX_RE.test(withHash) ? withHash.toLowerCase() : null;
}

const isHex = (v: unknown): v is string => typeof v === 'string' && HEX_RE.test(v.trim());

/** How long to sit on a colour-input drag before writing. The OS colour panel
 *  streams a value continuously while the pointer moves, and each write is a
 *  Babel parse plus an atomic file rewrite — for a SECTION it also cascades to
 *  every slide in that section. One commit per gesture, not per pixel. */
const COMMIT_DELAY_MS = 400;

/**
 * The one accent picker: the brand swatches, plus a custom colour (OS colour
 * chart + hex entry) for anything outside them. Current value ringed. Used by
 * the section header popover, the deck overview, the add-section form, the
 * library kebab and the Edit tab — so everything added here appears in all of
 * them at once.
 *
 * `value` is a brand token name or a `#rrggbb` hex. `onPick(value, meta)` fires
 * with `meta.custom === true` for a custom colour, which hosts use to keep a
 * popover OPEN while the user drags (closing it mid-gesture unmounts the input).
 *
 * When the value is inherited (per-slide accents only), pass `inheritedFrom` to
 * show where it comes from and `onReset` to render the reset affordance.
 */
type AccentSwatchesProps = {
  /** A brand token name OR a `#rrggbb` hex — the pickers have accepted both
   *  since the colour chart landed. */
  value?: string | null;
  /** `meta.custom` is true for a custom colour, which hosts use to keep a
   *  popover OPEN while the user drags (closing it unmounts the input). */
  onPick: (value: string, meta: { custom: boolean }) => void;
  disabled?: boolean;
  /** Per-slide accents only: where an inherited value comes from. */
  inheritedFrom?: string | null;
  onReset?: (() => void) | null;
  allowCustom?: boolean;
};

export default function AccentSwatches({
  value,
  onPick,
  disabled,
  inheritedFrom,
  onReset,
  allowCustom = true,
}: AccentSwatchesProps) {
  const custom = isHex(value);
  const [draft, setDraft] = React.useState(custom ? value.toLowerCase() : '');
  const [hexText, setHexText] = React.useState(custom ? value.toLowerCase() : '');
  const [invalid, setInvalid] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Follow the value when it changes underneath us (a token pick, another
  // surface's edit, a reload).
  React.useEffect(() => {
    if (isHex(value)) {
      setDraft(value.toLowerCase());
      setHexText(value.toLowerCase());
    } else {
      setDraft('');
      setHexText('');
    }
    setInvalid(false);
  }, [value]);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const commit = React.useCallback(
    (hex: string) => {
      clearTimeout(timer.current);
      if (disabled || !hex) return;
      // Nothing to write when it already IS the value — this is also what makes
      // an aimless drag around the colour wheel and back harmless.
      if (hex === (isHex(value) ? value.toLowerCase() : null)) return;
      onPick(hex, { custom: true });
    },
    [disabled, onPick, value],
  );

  const onColourInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = parseHex(e.target.value);
    if (!hex) return;
    setDraft(hex);
    setHexText(hex);
    setInvalid(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(hex), COMMIT_DELAY_MS);
  };

  const commitHexText = () => {
    if (!hexText.trim()) {
      setInvalid(false);
      return;
    }
    const hex = parseHex(hexText);
    if (!hex) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(hex);
    setHexText(hex);
    commit(hex);
  };

  const swatch = draft || (custom ? value.toLowerCase() : '');

  return (
    <div className="swatches">
      <div className="swatches__row" role="radiogroup" aria-label="Accent colour">
        {offeredTokens(value).map((token) => (
          <button
            key={token}
            type="button"
            role="radio"
            aria-checked={value === token}
            className={'swatches__dot' + (value === token ? ' swatches__dot--active' : '')}
            style={cssVars({ '--swatch': colourHex(token) })}
            onClick={() => !disabled && onPick(token, { custom: false })}
            disabled={disabled}
            title={accentName(token)}
            aria-label={accentName(token)}
          />
        ))}
      </div>

      {allowCustom && (
        // Deliberately OUTSIDE the radiogroup above: a colour input inside a
        // group of role="radio" children is invalid ARIA.
        <div className="swatches__custom" role="group" aria-label="Custom accent colour">
          <div className="swatches__custom-row">
            <label
              className={
                'swatches__dot swatches__dot--custom' +
                (swatch ? ' swatches__dot--has-colour' : '') +
                (custom ? ' swatches__dot--active' : '')
              }
              style={swatch ? cssVars({ '--swatch': swatch }) : undefined}
              title="Custom colour"
            >
              <input
                type="color"
                value={swatch || '#ff5a1f'}
                onChange={onColourInput}
                disabled={disabled}
                aria-label="Pick a custom accent colour"
              />
            </label>
            <input
              type="text"
              className="swatches__hex"
              value={hexText}
              placeholder="#faf553"
              spellCheck={false}
              maxLength={7}
              disabled={disabled}
              aria-label="Custom accent hex"
              aria-invalid={invalid || undefined}
              onChange={(e) => {
                setHexText(e.target.value);
                setInvalid(false);
              }}
              onBlur={commitHexText}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitHexText();
                }
              }}
            />
            {swatch && (
              // The ink the engine will actually pick. Worth showing: the APCA
              // rule is a lookup for brand accents and a luminance fallback for
              // everything else, and the fallback sits within ~0.001 of the
              // wrong answer for a saturated warm red.
              <span
                className="swatches__ink"
                style={cssVars({ '--swatch': swatch, '--ink': prefersDarkText(swatch) ? '#000' : '#f6f3ec' })}
                title={
                  prefersDarkText(swatch)
                    ? 'Text on this colour will be near-black'
                    : 'Text on this colour will be bone'
                }
                aria-hidden="true"
              >
                Aa
              </span>
            )}
          </div>
          {invalid && <div className="swatches__error small">Use a hex like #FAF553</div>}
        </div>
      )}

      {inheritedFrom && (
        <div className="swatches__meta muted small">
          {onReset ? (
            <>
              Overrides the section colour ·{' '}
              <button type="button" className="link" onClick={onReset} disabled={disabled}>
                Reset to section
              </button>
            </>
          ) : (
            <>Inherited from {inheritedFrom}</>
          )}
        </div>
      )}
      {!inheritedFrom && custom && <div className="swatches__meta muted small">Custom · {accentName(value)}</div>}
    </div>
  );
}
